<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <candidate-action-layout
      v-model:loading="isLoading"
      :candidate-id="candidateId"
      :title="
        meeting ? 'Schedule meeting' : $t('recruitment.schedule_interview')
      "
      @submit-valid="onSave"
    >
      <oxd-form-row>
        <oxd-grid :cols="3">
          <oxd-grid-item>
            <oxd-input-field
              v-model="interview.interviewName"
              :rules="rules.interviewName"
              :label="$t('recruitment.interview_title')"
              required
            />
          </oxd-grid-item>
          <!-- Orbit HRM: a candidate without a vacancy (a CV from the mailbox) gets one here -->
          <oxd-grid-item v-if="meeting && needsVacancy">
            <vacancy-dropdown
              v-model="vacancy"
              :status="true"
              :rules="rules.vacancy"
              required
            />
          </oxd-grid-item>
          <!-- Orbit HRM: where ANNA makes the meeting -->
          <oxd-grid-item v-if="meeting && platforms.length > 1">
            <oxd-input-field
              v-model="platform"
              type="select"
              label="Platform"
              :options="platforms"
              :rules="rules.platform"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item class="--offset-row-2">
            <interviewer-autocomplete
              v-for="(interviewer, index) in interviewers"
              :key="index"
              v-model="interviewers[index]"
              :show-delete="index > 0"
              :rules="
                rules.interviewerName.filter((_, i) => index === 0 || i > 0)
              "
              include-employees="onlyCurrent"
              required
              @remove="onRemoveInterviewer(index)"
            />
            <oxd-button
              icon-name="plus"
              display-type="text"
              class="orangehrm-input-field-bottom-space"
              :label="$t('general.add_another')"
              @click="onAddAnother"
            />
          </oxd-grid-item>
          <oxd-grid-item class="--offset-row-2">
            <date-input
              v-model="interview.interviewDate"
              :rules="rules.interviewDate"
              :label="$t('general.date')"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item class="--offset-row-2">
            <time-input
              v-model="interview.interviewTime"
              :step="5"
              :rules="rules.interviewTime"
              :label="$t('general.time')"
            />
          </oxd-grid-item>

          <oxd-grid-item class="--offset-row-3 --span-column-2">
            <oxd-input-field
              v-model="interview.note"
              :rules="rules.note"
              :label="$t('general.notes')"
              :placeholder="$t('general.type_here')"
              type="textarea"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-divider />
      <required-text></required-text>
      <oxd-form-actions>
        <oxd-button
          display-type="ghost"
          :label="$t('general.cancel')"
          @click="onClickBack"
        />
        <submit-button />
      </oxd-form-actions>
    </candidate-action-layout>
  </div>
</template>

<script>
import {
  required,
  validSelection,
  validDateFormat,
  validTimeFormat,
  shouldNotExceedCharLength,
} from '@/core/util/validation/rules';
import {navigate} from '@/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import CandidateActionLayout from '@/orangehrmRecruitmentPlugin/components/CandidateActionLayout.vue';
import InterviewerAutocomplete from '@/orangehrmRecruitmentPlugin/components/InterviewerAutocomplete.vue';
import VacancyDropdown from '@/orangehrmRecruitmentPlugin/components/VacancyDropdown';
import useDateFormat from '@/core/util/composable/useDateFormat';

// the platforms ANNA makes meetings on: Jitsi only for now (GoTo and Google Meet work in ANNA and
// its bot, put aside: add them back here to offer them)
const PLATFORMS = [{id: 'jitsi', label: 'Jitsi Meet'}];

const interviewModel = {
  interviewName: null,
  interviewDate: null,
  interviewTime: null,
  note: null,
};

export default {
  components: {
    'candidate-action-layout': CandidateActionLayout,
    'interviewer-autocomplete': InterviewerAutocomplete,
    'vacancy-dropdown': VacancyDropdown,
  },
  props: {
    candidateId: {
      type: Number,
      required: true,
    },
    // Orbit HRM: "Schedule meeting", outside the workflow (status unchanged)
    meeting: {
      type: Boolean,
      default: false,
    },
    // a meeting's default interviewer: Anna's employee record, {id, label}
    annaInterviewer: {
      type: Object,
      default: null,
    },
    // the candidate has no vacancy yet: the meeting attaches the one picked
    needsVacancy: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/recruitment/candidates/${props.candidateId}/${
        props.meeting ? 'meetings' : 'shedule-interview'
      }`,
    );
    const {userDateFormat} = useDateFormat();

    return {
      http,
      userDateFormat,
    };
  },
  data() {
    return {
      isLoading: false,
      platforms: PLATFORMS,
      platform: PLATFORMS[0],
      vacancy: null,
      interview: {
        ...interviewModel,
        interviewName: this.meeting
          ? 'Interview'
          : interviewModel.interviewName,
      },
      interviewers: [
        this.meeting && this.annaInterviewer
          ? {...this.annaInterviewer, isPastEmployee: false}
          : null,
      ],
      rules: {
        interviewName: [required, shouldNotExceedCharLength(100)],
        interviewDate: [required, validDateFormat(this.userDateFormat)],
        interviewTime: [validTimeFormat],
        interviewerName: [
          required,
          validSelection,
          (value) => {
            return this.interviewers.filter(
              (interviewer) => interviewer && interviewer.id === value?.id,
            ).length < 2
              ? true
              : this.$t('general.already_exists');
          },
        ],
        note: [shouldNotExceedCharLength(2000)],
        platform: [required],
        vacancy: [required],
      },
    };
  },
  methods: {
    onAddAnother() {
      this.interviewers.push(null);
    },
    onRemoveInterviewer(index) {
      this.interviewers.splice(index, 1);
    },
    onSave() {
      this.isLoading = true;
      this.http
        .create({
          ...this.interview,
          interviewerEmpNumbers: this.interviewers
            .map((interviewer) => interviewer?.id)
            .filter(Number),
          ...(this.meeting ? {platform: this.platform?.id} : {}),
          ...(this.needsVacancy ? {vacancyId: this.vacancy?.id} : {}),
        })
        .then(({data}) => {
          const goto = data?.meta?.goto;
          const invite = data?.meta?.invite;
          if (!this.meeting || !goto) return this.$toast.updateSuccess();
          // the candidate's email invitation: say if it went, or why not
          if (invite) {
            invite.sent
              ? this.$toast.success({
                  title: 'Invitation emailed',
                  message: invite.note || `Sent to ${invite.to}.`,
                })
              : this.$toast.warn({
                  title: 'No invitation emailed',
                  message: invite.note,
                });
          }
          // the meeting is saved either way; say whether ANNA made its link and Anna joins
          if (goto.error) {
            return this.$toast.warn({
              title: 'Meeting saved, no meeting link',
              message: goto.error,
            });
          }
          if (goto.callError) {
            return this.$toast.warn({
              title: 'Meeting link made, Anna will not join',
              message: goto.callError,
            });
          }
          return this.$toast.success({
            title: 'Meeting saved',
            message: `ANNA made the ${
              PLATFORMS.find((p) => p.id === goto.provider)?.label || 'online'
            } meeting. ${
              goto.call
                ? 'Anna will be in it a few minutes before the start.'
                : 'The link is on the Calendar.'
            }`,
          });
        })
        .then(() => {
          navigate('/recruitment/addCandidate/{id}', {id: this.candidateId});
        });
    },
    onClickBack() {
      navigate('/recruitment/addCandidate/{id}', {id: this.candidateId});
    },
  },
};
</script>
